// Authenticated community API client. Attaches the signed-in user's d3-auth
// bearer token to every request and resolves the base URL via getBackendUrl()
// (PUBLIC_API_URL).

import { getBackendUrl } from './backend.js';

/**
 * What this client calls itself in `X-D3-Client`. Must be one of the values the
 * portal recognises — anything else is dropped rather than stored, so a typo
 * here shows up as a comment with no attribution, not as an error.
 */
export const CLIENT_NAME = 'web';

/**
 * Read a JSON body, tolerating an empty one (a 204, or a handler that returns
 * no content). Returns null rather than throwing on an empty body.
 * @param {Response} response
 * @returns {Promise<any>}
 */
async function readJsonBody(response) {
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text);
}

/**
 * Fetch the public client configuration the SPA needs to run the sign-in flow.
 * This endpoint is unauthenticated — it necessarily has to be, since the SPA
 * reads it before it can hold a token.
 * @returns {Promise<object>}
 */
export async function fetchConfig() {
    const response = await fetch(`${getBackendUrl()}/config`);
    if (!response.ok) {
        throw new Error(`Failed to load client config: ${response.status} ${response.statusText}`);
    }
    return response.json();
}

class ApiClient {
    constructor() {
        this.baseUrl = getBackendUrl();
    }

    /**
     * Make a request with the bearer token attached when signed in.
     * @param {string} path
     * @param {RequestInit} [options]
     * @returns {Promise<Response>}
     */
    async fetch(path, options = {}) {
        const headers = new Headers(options.headers || {});
        // Declare what is writing. The portal records it on a comment and shows
        // it on the thread, so a reader can tell a person typing here from an
        // agent acting through the CLI. Sent on every request rather than only
        // the writes, so a future write path cannot forget it.
        headers.set('X-D3-Client', CLIENT_NAME);
        try {
            // Imported lazily: auth.js re-exports from lit.js, whose bare
            // `@dice-driven/d3-kit` specifier only the browser's import map
            // resolves. A static import would make this module — and every
            // module that reaches the API client — unloadable under `bun test`.
            const { authState } = await import('./auth.js');
            const token = await authState.getAccessToken();
            if (token) headers.set('Authorization', `Bearer ${token}`);
        } catch {
            // Not authenticated — send without a token; the server will 401.
        }
        if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
            headers.set('Content-Type', 'application/json');
        }
        return fetch(`${this.baseUrl}${path}`, { ...options, headers });
    }

    /**
     * @param {Response} response
     * @param {string} path
     */
    async _ensureOk(response, path) {
        if (response.ok) return;
        const body = await response.json().catch(() => ({}));
        const error = new Error(
            body.detail || body.message || `API error: ${response.status} ${response.statusText}`,
        );
        /** @type {any} */ (error).status = response.status;
        /** @type {any} */ (error).path = path;
        throw error;
    }

    async get(path) {
        const response = await this.fetch(path);
        await this._ensureOk(response, path);
        return readJsonBody(response);
    }

    async post(path, data) {
        const options = { method: 'POST' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        await this._ensureOk(response, path);
        return readJsonBody(response);
    }

    async put(path, data) {
        const options = { method: 'PUT' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        await this._ensureOk(response, path);
        return readJsonBody(response);
    }

    /**
     * POST a file as multipart/form-data.
     *
     * Separate from post() because the body must be a FormData, and the
     * Content-Type header must NOT be set: the browser writes it itself,
     * including the multipart boundary, which we cannot know here.
     * @param {string} path
     * @param {File|Blob} file
     * @param {string} [filename]
     */
    async postFile(path, file, filename) {
        const form = new FormData();
        form.append('file', file, filename ?? file.name ?? 'screenshot.png');
        const response = await this.fetch(path, { method: 'POST', body: form });
        await this._ensureOk(response, path);
        return readJsonBody(response);
    }

    async patch(path, data) {
        const options = { method: 'PATCH' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        await this._ensureOk(response, path);
        return readJsonBody(response);
    }

    async delete(path) {
        const response = await this.fetch(path, { method: 'DELETE' });
        await this._ensureOk(response, path);
    }
}

export const api = new ApiClient();
